/**
 * Vue directive to block non-Western characters in inputs
 * Allows only ASCII characters (0x20-0x7E) + common Latin accents
 * Uses 'input' event instead of 'keydown' to work correctly with IME (Input Method Editor)
 * for CJK (Chinese, Japanese, Korean) keyboards and other non-Western keyboards
 */

import type { DirectiveBinding, App } from 'vue'

/**
 * Regex that allows:
 * - Basic ASCII: \x20-\x7E (space to ~)
 * - Latin accents: \u00C0-\u017F (À-ÿ)
 * - Unicode spaces: \u00A0-\u00FF (non-breaking spaces and other Latin characters)
 */
const ASCII_LATIN_REGEX = /[\x20-\x7E\u00C0-\u017F\u00A0-\u00FF]/g

/**
 * Filters non-allowed characters from the input value
 */
function filterNonAsciiLatin(value: string): string {
  if (!value) return ''
  
  // Extract only allowed characters
  const matches = value.match(ASCII_LATIN_REGEX)
  return matches ? matches.join('') : ''
}

/**
 * Finds the actual input element inside a Vuetify component or HTML element
 */
function findInputElement(el: HTMLElement): HTMLInputElement | HTMLTextAreaElement | null {
  // If it's already an input or textarea, return directly
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return el
  }
  
  // Search for input or textarea inside the element (for Vuetify components)
  const input = el.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null
  return input || null
}

/**
 * Directive that blocks non-Western characters
 */
export const vAsciiOnly = {
  mounted(el: HTMLElement) {
    // Find the actual input (may be inside a Vuetify component)
    const inputElement = findInputElement(el)
    
    if (!inputElement) {
      console.warn('v-ascii-only: Could not find input element')
      return
    }
    
    const handleInput = (e: Event) => {
      const input = e.target as HTMLInputElement | HTMLTextAreaElement
      const originalValue = input.value
      const filteredValue = filterNonAsciiLatin(originalValue)
      
      // If the value was changed, update the input
      if (originalValue !== filteredValue) {
        // Save cursor position
        const cursorPosition = input.selectionStart || 0
        const lengthDiff = originalValue.length - filteredValue.length
        
        // Update the value
        input.value = filteredValue
        
        // Restore cursor position (adjusted by length difference)
        const newPosition = Math.max(0, cursorPosition - lengthDiff)
        input.setSelectionRange(newPosition, newPosition)
        
        // Dispatch event to update Vue's v-model
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
    
    // Add listener to 'input' event (works with IME)
    inputElement.addEventListener('input', handleInput)
    
    // Store the function and element for cleanup
    ;(el as any)._asciiOnlyHandler = handleInput
    ;(el as any)._asciiOnlyInputElement = inputElement
  },
  
  unmounted(el: HTMLElement) {
    // Remove listener when component is unmounted
    const handler = (el as any)._asciiOnlyHandler
    const inputElement = (el as any)._asciiOnlyInputElement
    
    if (handler && inputElement) {
      inputElement.removeEventListener('input', handler)
      delete (el as any)._asciiOnlyHandler
      delete (el as any)._asciiOnlyInputElement
    }
  }
}

/**
 * Helper function to register the directive globally
 */
export function registerAsciiOnlyDirective(app: App) {
  app.directive('ascii-only', vAsciiOnly)
}
